// Tenant notification policy — category × channel, each cell a 3-way select
// Required / Optional / Off. Served by notification-service through the
// gateway; tenant admins only (the org Settings page is already admin-gated;
// a 403 still surfaces the backend message). Locked cells (platform-required)
// render disabled with "Always on". Every change saves on its own: optimistic
// set, revert + toast when the PUT fails. "Optional" hands the choice to each
// person in their own settings (NotificationPreferencesCard).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import {
  NOTIFICATION_CHANNELS, NOTIFICATION_POLICY_MODES, getNotificationPolicy, updateNotificationPolicy,
  type NotificationChannel, type NotificationPolicyCategory, type NotificationPolicyMode,
} from '@/services/notificationPreferencesService';

const setMode = (
  cats: NotificationPolicyCategory[], category: string, channel: NotificationChannel, mode: NotificationPolicyMode,
): NotificationPolicyCategory[] =>
  cats.map((c) => (c.key === category
    ? { ...c, channels: { ...c.channels, [channel]: { ...c.channels[channel], mode } } }
    : c));

export const NotificationPolicyCard: React.FC = () => {
  const { t } = useTranslation('settings');
  const k = (key: string, dflt: string, opts: Record<string, unknown> = {}) => t(`notif_policy.${key}`, { defaultValue: dflt, ...opts }) as string;
  const [categories, setCategories] = useState<NotificationPolicyCategory[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    try { setCategories((await getNotificationPolicy()).categories); setError(null); }
    catch (e) { setError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const channelLabel: Record<NotificationChannel, string> = {
    in_app: k('channel_in_app', 'In-app'),
    push: k('channel_push', 'Push'),
    email: k('channel_email', 'Email'),
    // ⚠️ Was MISSING, so the table rendered a fourth dropdown with an empty
    // header: an admin could set the company-wide desktop policy without being
    // told which channel they were setting. Record<NotificationChannel,string>
    // made this a compile error the whole time — the build is vite-only, so
    // nobody saw it. Wording matches NotificationPreferencesCard: the person is
    // choosing WHERE a notification appears, not which protocol carries it.
    web_push: k('channel_web_push', 'Desktop'),
  };
  const modeLabel: Record<NotificationPolicyMode, string> = {
    required: k('mode_required', 'Required'),
    optional: k('mode_optional', 'Optional'),
    off: k('mode_off', 'Off'),
  };

  const change = async (category: string, channel: NotificationChannel, mode: NotificationPolicyMode, prev: NotificationPolicyMode) => {
    if (mode === prev) return;
    const id = `${category}:${channel}`;
    setCategories((cs) => (cs ? setMode(cs, category, channel, mode) : cs));
    setPending((p) => new Set(p).add(id));
    try {
      const next = await updateNotificationPolicy([{ category, channel, mode }]);
      if (next.categories.length) setCategories(next.categories);
    } catch (e) {
      setCategories((cs) => (cs ? setMode(cs, category, channel, prev) : cs));
      toast.error(getApiErrorMessage(e, k('save_failed', 'Could not save that policy.')));
    } finally {
      setPending((p) => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  return (
    <Card data-testid="notification-policy">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> {k('title', 'Notification policy')}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {k('subtitle', 'Decide, per kind of update and channel, whether notifications are required, optional or off for everyone in the company. Optional lets each person choose in their own settings.')}
        </p>
      </CardHeader>
      <CardContent>
        {loading && !categories && (
          <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 animate-spin" /> {k('loading', 'Loading…')}
          </p>
        )}
        {!loading && !!error && (
          <div className="text-sm text-muted-foreground flex items-center justify-between gap-3" role="alert">
            <span>{getApiErrorMessage(error, k('load_failed', 'Could not load the notification policy.'))}</span>
            <Button variant="outline" size="sm" onClick={() => load()}>{k('retry', 'Retry')}</Button>
          </div>
        )}
        {!loading && !error && categories && categories.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="notification-policy-empty">{k('empty', 'No notification categories are available yet.')}</p>
        )}
        {categories && categories.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th scope="col" className="text-left font-medium pb-2 pr-3">{k('category', 'Category')}</th>
                  {NOTIFICATION_CHANNELS.map((ch) => (
                    <th key={ch} scope="col" className="font-medium pb-2 px-2 text-center whitespace-nowrap">{channelLabel[ch]}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {categories.map((c) => (
                  <tr key={c.key} data-testid={`policy-cat-${c.key}`}>
                    <td className="py-3 pr-3 align-top">
                      <div className="font-medium text-charcoal">{c.label}</div>
                      {c.description && <p className="text-xs text-muted-foreground mt-0.5">{c.description}</p>}
                    </td>
                    {NOTIFICATION_CHANNELS.map((ch) => {
                      const cell = c.channels?.[ch] ?? { mode: 'off' as NotificationPolicyMode, locked: false };
                      const busy = pending.has(`${c.key}:${ch}`);
                      return (
                        <td key={ch} className="py-3 px-2 text-center align-top">
                          <div className="inline-flex flex-col items-center gap-1">
                            <select value={cell.mode} disabled={cell.locked || busy}
                              aria-label={`${c.label} — ${channelLabel[ch]}`}
                              className="rounded-md border border-border bg-background text-foreground text-sm px-2 py-1 disabled:opacity-60"
                              onChange={(e) => change(c.key, ch, e.target.value as NotificationPolicyMode, cell.mode)}>
                              {NOTIFICATION_POLICY_MODES.map((m) => <option key={m} value={m}>{modeLabel[m]}</option>)}
                            </select>
                            {cell.locked && <span className="text-[10px] text-muted-foreground">{k('always_on', 'Always on')}</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-3">{k('optional_hint', 'Optional lets each person choose in their own settings. Required is always delivered; Off is never sent.')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationPolicyCard;
